import { useCallback, useEffect } from "react";
import { usePipelinesIds } from "src/pages/ComparativeAnalysisPage/ComparativeAnalysis.queries";
import { useFormStateAtoms } from "../ComparativeAnalysisForm.atoms";
import { DEFAULT_TASK } from "../ComparativeAnalysisForm.constants";
import type {
  ComparisonHistoryPayload,
  GroupProps,
  SearchParamsType,
} from "../ComparativeAnalysisForm.types";
import { getFlatFormStateValues } from "../ComparativeAnalysisForm.utils";
import { useComparativeAnalysisSearchParams } from "./useComparativeAnalysisSearchParams";
import { useUpdateSearchParams } from "./useUpdateSearchParams";

type LocalFormGroupsStateReturnType = {
  group1Props: GroupProps;
  group2Props: GroupProps;
  syncLocalStateToURL: () => ComparisonHistoryPayload;
};

// the form uses local-state atoms.
// URL search-params are only updated when the form is submitted
export const useLocalFormGroupsState = (): LocalFormGroupsStateReturnType => {
  const updateSearchParams = useUpdateSearchParams();
  const formState = useFormStateAtoms();

  // fetch the pipelines' ids (by pipeline name)
  const {
    p1Id,
    p2Id,
    isFetched: pipelinesIdsFetched,
  } = usePipelinesIds({
    env1: formState.env1[0],
    env2: formState.env2[0],
    p1Name: formState.pipeline1[0],
    p2Name: formState.pipeline2[0],
  });

  // sync the local state (atoms) with the URL search params (only when the form is submitted)
  const syncLocalStateToURL = useCallback(() => {
    const flat = getFlatFormStateValues(formState);

    if (flat.task1 === DEFAULT_TASK) {
      flat.task1 = "";
    }
    if (flat.task2 === DEFAULT_TASK) {
      flat.task2 = "";
    }

    const { pipelineId1, pipelineId2, ...forUrl } = flat;
    updateSearchParams(forUrl);

    const item: ComparisonHistoryPayload = {
      env1: flat.env1 ?? "",
      env2: flat.env2 ?? "",
      pipeline1: flat.pipeline1 ?? "",
      pipeline2: flat.pipeline2 ?? "",
      pit1: flat.pit1 ?? "",
      pit2: flat.pit2 ?? "",
      task1: flat.task1,
      task2: flat.task2,
      pipelineId1: pipelineId1?.toString() ?? "",
      pipelineId2: pipelineId2?.toString() ?? "",
    };
    return item;
  }, [formState, updateSearchParams]);

  // update the pipeline ids' local state when a pipeline's name changes
  // Pre-existing: p1Id/p2Id are intentionally omitted from deps — refetch after isFetched can leave
  // IDs stale until a follow-up fix adds them (see CA URL refactor plan).
  useEffect(() => {
    if (pipelinesIdsFetched) {
      formState.pipelineId1[1](p1Id?.toString());
      formState.pipelineId2[1](p2Id?.toString());
    }
  }, [pipelinesIdsFetched, formState.pipelineId1, formState.pipelineId2]);

  // update the env2 local state when the env1 changes
  // (only if the env2 is not already set)
  useEffect(() => {
    if (formState.env1[0] && !formState.env2[0]) {
      formState.env2[1](formState.env1[0]);
    }
  }, [formState.env1[0]]);

  // update the pipeline2 local state when the pipeline1 changes
  // (only if the pipeline2 is not already set)
  useEffect(() => {
    const baseEnv = formState.env1[0];
    const compareEnv = formState.env2[0];

    const basePipeline = formState.pipeline1[0];
    const comparePipeline = formState.pipeline2[0];

    if (basePipeline && !comparePipeline && baseEnv === compareEnv) {
      formState.pipeline2[1](basePipeline);
    }
  }, [formState.pipeline1[0], formState.pipeline2[0]]);

  return {
    syncLocalStateToURL,

    // base group props
    group1Props: {
      env: formState.env1[0],
      setEnv: formState.env1[1],

      pipeline: formState.pipeline1[0],
      setPipeline: formState.pipeline1[1],

      pipelineId: formState.pipelineId1[0]?.toString(),
      setPipelineId: formState.pipelineId1[1],

      pit: formState.pit1[0],
      setPit: formState.pit1[1],

      task: formState.task1[0],
      setTask: formState.task1[1],
    },

    // compare group props
    group2Props: {
      env: formState.env2[0],
      setEnv: formState.env2[1],

      pipeline: formState.pipeline2[0],
      setPipeline: formState.pipeline2[1],

      pipelineId: formState.pipelineId2[0]?.toString(),
      setPipelineId: formState.pipelineId2[1],

      pit: formState.pit2[0],
      setPit: formState.pit2[1],

      task: formState.task2[0],
      setTask: formState.task2[1],
    },
  };
};

/**
 * @returns a function that syncs the URL search-params groups to the form's local-state atoms.
 * (this is an abstraction over `useSetLocalFormGroupsState`)
 */
export const useSyncUrlToLocalState = () => {
  const setLocalFormState = useSetLocalFormState();

  // URL search-params
  const formSearchParams = useComparativeAnalysisSearchParams();

  return useCallback(() => {
    setLocalFormState(formSearchParams);
  }, [setLocalFormState, formSearchParams]);
};

/**
 * @returns a function which gets `params` flat object and updates the local-state atoms
 */
export const useSetLocalFormState = () => {
  const stateAtoms = useFormStateAtoms();

  // (only the atoms' setters used and they are stable)
  // hence no need for any hook dependencies
  return useCallback(
    (
      params: Partial<SearchParamsType> & {
        pipelineId1?: string;
        pipelineId2?: string;
      }
    ) => {
      for (const p in params) {
        stateAtoms[p as keyof typeof stateAtoms]?.[1]?.(
          params[p as keyof typeof params]
        );
      }
    },
    []
  );
};
